import { DateTime } from "luxon"
import type { FC, ReactNode } from "react"
import { useState } from "react"
import Link from "~/components/Link"
import FooterBrand from "~/icons/FooterBrand"
import FooterFacebook from "~/icons/FooterFacebook"
import FooterInstagram from "~/icons/FooterInstagram"
import FooterLinkedIn from "~/icons/FooterLinkedIn"
import FooterTikTok from "~/icons/FooterTikTok"
import FooterX from "~/icons/FooterX"
import useNotifications from "~/hooks/useNotifications"
import { useAnalytics } from "../providers/AnalyticsProvider"
import { preventDefault } from "~/helpers/prevent-default"
import type { SerializeFrom } from "@remix-run/node"
import type { WithContentfulHeaderLocationFragment } from "~/contentful"
import { SubscriberCreateDocument } from "~/graphql"
import { useMutation } from "@apollo/client/index.js"
import {
  getLocationCardHeadline,
  resolveLocationRouteSlug
} from "~/helpers/location"
import { ANALYTICS_EVENTS } from "~/helpers/analyticsEvents"

interface Props {
  locationPages: SerializeFrom<WithContentfulHeaderLocationFragment>[]
}

const Footer: FC<Props> = ({ locationPages }) => {
  return (
    <footer className="bg-camp-black">
      <div className="mx-auto px-4 py-6 lg:container lg:px-6 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3">
          <div className="order-2 mt-12 flex flex-col items-center justify-between lg:order-1 lg:col-span-1 lg:mt-0 lg:items-start">
            <div className="flex-grow">
              <FooterBrand className="h-[142px] w-[179px] object-contain" />
            </div>

            <FooterLink
              url="https://cert.privo.com/#/companies/campnyc"
              external
            >
              <img
                src="/images/COPPA.webp"
                loading="lazy"
                width={57}
                height={70}
                className="mt-6 lg:ml-5"
                alt="Kids Privacy assured by Privo, click to verify"
              />
            </FooterLink>

            <p className="mt-6 text-xs text-white/60 lg:ml-5">
              ©{DateTime.now().year} Camp NYC, Inc.
            </p>
          </div>
          <div className="order-1 col-span-2 lg:order-2">
            <div className="grid grid-cols-2 gap-x-5 lg:grid-cols-3">
              <div className="hidden lg:col-span-1 lg:block">
                <h3 className="mb-6 font-serif text-white">CAMP Locations</h3>

                <ul className="space-y-3 text-white/60">
                  {locationPages.map(location => (
                    <li key={location.sys.id}>
                      <FooterLink
                        url={
                          location.slug
                            ? `/${resolveLocationRouteSlug(location.slug)}`
                            : null
                        }
                      >
                        {getLocationCardHeadline(location.name)}
                      </FooterLink>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="mb-6 font-serif text-white">Other Pages</h3>

                <ul className="space-y-3 text-white/60">
                  <li>
                    <FooterLink url="/help-center" ariaLabel="Help Center">
                      FAQ
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/about-us" ariaLabel="About Camp">
                      About
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/guides">Guides</FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/gift-cards">Gift Cards</FooterLink>
                  </li>

                  <li>
                    <FooterLink url="/gift-card-terms-and-conditions">
                      Gift Card Terms of Service
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/shipping-and-returns">
                      Shipping and Returns
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/terms-of-service">
                      Terms of Service
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/privacy-policy">
                      Privacy Policy
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/careers">Careers</FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/press-and-partnerships">
                      Press and Partnerships
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/data-requests/new">
                      Manage Your Personal Information
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/contact">Contact</FooterLink>
                  </li>
                  <li>
                    <FooterLink
                      url="https://www.cigna.com/legal/compliance/machine-readable-files"
                      external
                    >
                      CIGNA Coverage Transparency Statement
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink url="/camp-club">CAMP Club</FooterLink>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="mb-6 font-serif text-white">Follow Us</h3>

                <ul className="space-y-3 text-white/60">
                  <li>
                    <FooterLink
                      url="https://tiktok.com/@campstores"
                      ariaLabel="TikTok"
                    >
                      <FooterTikTok aria-hidden="true" />

                      <span aria-hidden="true">TikTok</span>
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink
                      url="https://instagram.com/campstores"
                      ariaLabel="Instagram"
                    >
                      <FooterInstagram aria-hidden="true" />

                      <span aria-hidden="true">Instagram</span>
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink
                      url="https://facebook.com/campstores"
                      ariaLabel="Facebook"
                    >
                      <FooterFacebook aria-hidden="true" />

                      <span aria-hidden="true">Facebook</span>
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink
                      url="https://www.linkedin.com/company/campstores"
                      ariaLabel="LinkedIn"
                    >
                      <FooterLinkedIn aria-hidden="true" />

                      <span aria-hidden="true">LinkedIn</span>
                    </FooterLink>
                  </li>
                  <li>
                    <FooterLink
                      url="https://x.com/campstores"
                      ariaLabel="X (formerly Twitter)"
                    >
                      <FooterX aria-hidden="true" />

                      <span aria-hidden="true">X</span>
                    </FooterLink>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-3">
              <div className="col-span-3 lg:col-span-2">
                <div className="mb-3 font-serif text-base text-white">
                  Get Our Newsletter
                </div>

                <FooterSubscribeForm />
              </div>
              <div className="col-span-1"></div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

const FooterLink: FC<{
  url: string | null
  external?: boolean
  ariaLabel?: string
  children?: ReactNode
}> = ({ url, external, ariaLabel, children }) => (
  <Link
    to={url}
    className="inline-flex items-center space-x-2 font-sans text-base transition-colors duration-200 hover:text-white focus:outline-white"
    aria-label={ariaLabel}
    {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
  >
    {children}
  </Link>
)

const FooterSubscribeForm: FC = () => {
  const { showSuccess } = useNotifications()
  const { track, identify } = useAnalytics()

  const [create, { loading }] = useMutation(SubscriberCreateDocument, {
    onCompleted: () => {
      identify({
        email
      })
      track(ANALYTICS_EVENTS.SUBSCRIBE_FORM_SUBMITTED)
      showSuccess("You're all set! We'll be in touch.")
      setEmail("")
    }
  })
  const [email, setEmail] = useState("")

  const handleSubmit = () =>
    create({
      variables: {
        input: {
          email,
          origin: "footer"
        }
      }
    })

  return (
    <form onSubmit={preventDefault(handleSubmit)}>
      <div className="flex flex-grow items-center justify-between">
        <input
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="h-input flex h-12 flex-grow bg-white px-4 text-sm text-gray-500 placeholder-gray-500/60 focus:ring"
          placeholder="Email address"
          aria-label="Email"
          type="email"
          required
        />

        <button
          type="submit"
          disabled={loading}
          className="flex h-12 items-center justify-center bg-red-500 pl-5 pr-2 text-white focus:ring"
        >
          <span className="mr-4 text-sm">Sign Up</span>
        </button>
      </div>

      <p className="mt-3 text-xs leading-5 text-white/60">
        By providing my email address I agree that I have reviewed and agree to
        the{" "}
        <span className="underline font-semibold">
          <Link
            className="text-white focus:outline-white"
            to="/terms-of-service"
          >
            Terms of Service
          </Link>{" "}
        </span>
        and{" "}
        <span className="underline font-semibold">
          <Link className="text-white focus:outline-white" to="/privacy-policy">
            Privacy Policy
          </Link>
        </span>
        .
      </p>
    </form>
  )
}

export default Footer
